/**
 * Step 2's choice with its defaults applied, plus the spec the later steps read.
 *
 * Defaults are derived on render, never copied into state: a prefilled agent
 * (from its page, or a saved draft) fills in its project and is matched to its
 * target by `agent_id`, but only through a connection that reaches it (same
 * platform, same base URL); a connection with one project picks it. The spec is
 * read live from the platform for the chosen version, or built from what the
 * person declared when the platform cannot describe its targets.
 */

import type { Agent, AgentSpec } from "@/features/agents/types";
import type { Connector } from "./types";
import type { Connection } from "@/features/settings/types";
import { useDiscoveredSpec, useGroups, useTargets } from "./hooks";
import { screensFor } from "./registry";
import type { ResolvedTarget, TargetDraft, TargetPayload } from "./types";

/** The draft without its `undefined` fields, so they do not hide a prefilled value. */
function given(draft: TargetDraft): Partial<TargetDraft> {
  return Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== undefined));
}

export interface SpecState {
  data: AgentSpec | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

export function useTarget(
  connection: Connection | undefined,
  connector: Connector | undefined,
  draft: TargetDraft,
  prefill: Agent | undefined,
) {
  const screens = connector ? screensFor(connector) : null;
  const id = screens?.discovers ? (connection?.id ?? null) : null;
  const reaches =
    !!prefill &&
    !!connection &&
    prefill.connector_type === connection.connector_type &&
    prefill.base_url === connection.base_url;
  const merged = { ...(reaches && screens ? screens.fromRef(prefill.external_ref) : {}), ...given(draft) };

  const groups = useGroups(id);
  const only = groups.data?.length === 1 ? groups.data[0]!.id : null;
  const group = merged.group !== undefined ? merged.group : only;
  const targets = useTargets(id, group);
  const matched = reaches ? (targets.data?.find((t) => t.agent_id === prefill.id)?.name ?? null) : null;
  const target = merged.target !== undefined ? merged.target : matched;
  const agentId = id
    ? (targets.data?.find((t) => t.name === target)?.agent_id ?? null)
    : reaches
      ? prefill.id
      : null;
  const resolved: ResolvedTarget = { ...merged, version: draft.version, group, target, agentId };

  const discovered = useDiscoveredSpec(id, group, target, draft.version);
  const spec: SpecState =
    screens && !screens.discovers && screens.declaredSpec
      ? { data: screens.declaredSpec(resolved), isPending: false, isError: false, error: null }
      : discovered;
  const ready = !!connection && !!screens && screens.ready(resolved);
  const payload = (): TargetPayload | null =>
    connection && screens ? { connection_id: connection.id, ...screens.payload(resolved) } : null;

  return { screens, resolved, spec, ready, payload };
}

export type TargetState = ReturnType<typeof useTarget>;
